import { Button, IconFont, Menu } from '@byecode/ui'
import type { ActorEvent } from '@lighthouse/core'
import { type ButtonItem, type ButtonSizeType, EVENT_TYPE, type RichTextContentProtocol, type Shape } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { useActionRunningLoadings } from '../../hooks'
import { ActionButton } from '../ActionButton'

interface TailButtonGroupProps {
    uniqueId: string
    style?: React.CSSProperties
    size?: ButtonSizeType
    group?: ButtonItem[]
    shape?: Shape
    radius?: string
    onActionTrigger?: (id: string, params: ActorEvent) => Promise<void>
    onRenderTitle: (v: RichTextContentProtocol) => string
}

const SCxButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
    text-align: left;
    color: var(--color-gray-900);
`
const EXTRA_BUTTON_SIZE_MAP = new Map<ButtonSizeType, number>([
    ['xxl', 62],
    ['xl', 50],
    ['lg', 42],
    ['md', 38],
    ['sm', 34],
    ['xs', 30]
])

export const TailActionButtonGroup: React.FC<TailButtonGroupProps> = ({
    uniqueId,
    group,
    size,
    radius = '8',
    shape = 'rectangle',
    style,
    onActionTrigger,
    onRenderTitle
}) => {
    const extraButtonSize = EXTRA_BUTTON_SIZE_MAP.get(size ?? 'sm')
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()

    const handleClick = useCallback(
        (actorEvent: ActorEvent | undefined, id: string) => {
            handleActionTriggerWithLoading({ actorEvent, id: `${uniqueId}-${id}`, trigger: onActionTrigger })
        },
        [handleActionTriggerWithLoading, onActionTrigger, uniqueId]
    )

    return useMemo(() => {
        const actionList = group
        if (!actionList?.length) {
            return null
        }
        const firstTwoActions = actionList.slice(0, 2)
        const extraActions = actionList.slice(2)
        return (
            <SCxButtonsContainer style={style}>
                {firstTwoActions.map(item => {
                    const { eventList, id } = item
                    const actorEvent = eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
                    return (
                        <ActionButton
                            key={id}
                            size={size}
                            radius={shape === 'rectangle' ? radius : '100'}
                            config={item}
                            // loading={loadings[id]}
                            disabled={loadings[id]}
                            onClick={() => handleClick(actorEvent, id)}
                            onRenderTitle={onRenderTitle}
                        />
                    )
                })}
                {extraActions.length > 0 && (
                    <Menu width={180} position="bottom-end" closeOnItemClick>
                        <Menu.Target>
                            <Button
                                radius={100}
                                style={{ width: extraButtonSize, height: extraButtonSize }}
                                icon={<IconFont type="DotsThree" />}
                            />
                        </Menu.Target>
                        <Menu.Dropdown styles={{ dropdown: { maxHeight: 200, overflowY: 'auto' } }}>
                            {extraActions.map(item => {
                                const { eventList, name, id } = item
                                const actorEvent = eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
                                return (
                                    <Menu.Item
                                        key={id}
                                        disabled={loadings[id]}
                                        onClick={() => handleActionTriggerWithLoading({ actorEvent, id, trigger: onActionTrigger })}
                                    >
                                        {onRenderTitle(name)}
                                    </Menu.Item>
                                )
                            })}
                        </Menu.Dropdown>
                    </Menu>
                )}
            </SCxButtonsContainer>
        )
    }, [
        group,
        style,
        extraButtonSize,
        size,
        shape,
        radius,
        loadings,
        onRenderTitle,
        handleClick,
        handleActionTriggerWithLoading,
        onActionTrigger
    ])
}
